import React, { type JSX } from "react";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";

/** Tooltip explaining why the composer's buttons are unavailable. */
function disabledReason(projectReady: boolean, useCaseSelected: boolean): string | undefined {
  if (!projectReady) return "Waiting for project setup";
  if (!useCaseSelected) return "Please select a use case to start";
  return undefined;
}

/** Text input field - mobile optimized */
function MessageTextarea({
  ariaLabel,
  inputText,
  useCaseSelected,
  onInputChange,
  onKeyDown,
}: {
  ariaLabel: string;
  inputText: string;
  useCaseSelected: boolean;
  onInputChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}): JSX.Element {
  return (
    <textarea
      value={inputText}
      onChange={(e) => onInputChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder="Ask DealOS..."
      disabled={!useCaseSelected}
      rows={1}
      className={`flex-1 px-3 sm:px-4 py-3 sm:py-4 border rounded-lg focus:outline-none text-base min-h-[48px] sm:min-h-[52px] max-h-[132px] touch-manipulation font-cvcBody resize-none overflow-y-auto ${
        useCaseSelected
          ? "border-cvc-grey focus:ring-2 focus:ring-cvc-blue focus:border-transparent text-cvc-text placeholder-cvc-text/50 bg-white"
          : "border-cvc-grey bg-white text-gray-400 placeholder-gray-400 cursor-not-allowed"
      }`}
      style={{ fieldSizing: "content" } as React.CSSProperties}
      aria-label={ariaLabel}
      autoComplete="off"
      autoCapitalize="sentences"
      autoCorrect="on"
    />
  );
}

/** Send button - touch-friendly size */
function SendButton({
  sendDisabled,
  chatInputReady,
  useCaseSelected,
  projectReady,
}: {
  sendDisabled: boolean;
  chatInputReady: boolean;
  useCaseSelected: boolean;
  projectReady: boolean;
}): JSX.Element {
  return (
    <button
      type="submit"
      disabled={sendDisabled}
      className="bg-cvc-blue hover:opacity-90 disabled:bg-cvc-grey disabled:cursor-not-allowed text-white font-medium p-3 sm:p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-cvc-blue focus:ring-opacity-50 transition-all duration-200 flex items-center justify-center min-h-[48px] min-w-[48px] sm:min-h-[52px] sm:min-w-[52px] touch-manipulation"
      aria-label="Send message button"
      aria-disabled={!chatInputReady || !useCaseSelected}
      aria-busy={!chatInputReady}
      title={disabledReason(projectReady, useCaseSelected)}
    >
      <PaperAirplaneIcon className="h-5 w-5 sm:h-6 sm:w-6" />
    </button>
  );
}

interface ChatComposerFormProps {
  ariaLabel: string;
  inputText: string;
  projectReady: boolean;
  chatInputReady: boolean;
  useCaseSelected: boolean;
  sendDisabled: boolean;
  onInputChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSubmit: () => void;
}

/** The composer row: textarea and send. */
export default function ChatComposerForm({
  ariaLabel,
  inputText,
  projectReady,
  chatInputReady,
  useCaseSelected,
  sendDisabled,
  onInputChange,
  onKeyDown,
  onSubmit,
}: ChatComposerFormProps): JSX.Element {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="flex items-center gap-2 sm:gap-3"
    >
      <MessageTextarea
        ariaLabel={ariaLabel}
        inputText={inputText}
        useCaseSelected={useCaseSelected}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
      />

      <SendButton
        sendDisabled={sendDisabled}
        chatInputReady={chatInputReady}
        useCaseSelected={useCaseSelected}
        projectReady={projectReady}
      />
    </form>
  );
}
